import React, { useState, useEffect, useRef, useCallback } from 'react';
import { geocode, reverseGeocode } from '../utils/api';
import type { GeocodeResult } from '../types/api';

interface AddressSearchProps {
  label: string;
  value: { lat: number; lon: number } | null;
  onChange: (loc: { lat: number; lon: number } | null) => void;
  showLocButton?: boolean;
  onLocClick?: () => void;
  userLocation?: { lat: number; lon: number } | null;
  nearPoint?: { lat: number; lon: number } | null;
  placeholder?: string;
}

function shortAddress(displayName: string): string {
  const parts = displayName.split(',').map((s: string) => s.trim());
  if (parts.length <= 2) return parts.join(', ');
  return parts.slice(0, 2).join(', ');
}

function dropdownLabel(displayName: string): string {
  const parts = displayName.split(',').map((s: string) => s.trim());
  const primary = parts[0] || '';
  const secondary = parts.slice(1, 4).join(', ');
  return secondary ? `${primary}, ${secondary}` : primary;
}

export default function AddressSearch({
  label,
  value,
  onChange,
  showLocButton,
  onLocClick,
  userLocation,
  nearPoint,
  placeholder = 'Search address or enter lat, lon',
}: AddressSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);
  const isInternalRef = useRef(false);
  const latestRevRef = useRef(0);

  // Sync display when value changes externally (e.g. map click)
  useEffect(() => {
    if (!value) return;
    if (isInternalRef.current) {
      isInternalRef.current = false;
      return;
    }
    const id = ++latestRevRef.current;
    reverseGeocode(value.lat, value.lon).then((name) => {
      if (id !== latestRevRef.current) return;
      setQuery(name ? shortAddress(name) : `${value.lat.toFixed(5)}, ${value.lon.toFixed(5)}`);
    });
  }, [value?.lat, value?.lon]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = useCallback((text: string) => {
    setQuery(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Allow raw lat, lon input
    const coordMatch = text.match(/^\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lon = parseFloat(coordMatch[2]);
      if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        isInternalRef.current = true;
        onChange({ lat, lon });
        setResults([]);
        setShowDropdown(false);
        return;
      }
    }

    if (text.trim().length < 3) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    const near = nearPoint || userLocation || null;

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await geocode(text, 10, near);
        setResults(r);
        setShowDropdown(r.length > 0);
      } catch {
        setResults([]);
      }
      setSearching(false);
    }, 300);
  }, [onChange, userLocation, value]);

  const handleSelect = (result: GeocodeResult) => {
    isInternalRef.current = true;
    setQuery(shortAddress(result.display_name));
    setShowDropdown(false);
    onChange({ lat: result.lat, lon: result.lon });
  };

  const handleClear = () => {
    isInternalRef.current = true;
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    onChange(null);
  };

  return (
    <div className="address-search" ref={containerRef} style={{ position: 'relative' }}>
      <label>{label}</label>
      <div className="search-row">
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <input
            type="text"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
            placeholder={placeholder}
            style={{ width: '100%', paddingRight: 28, boxSizing: 'border-box' }}
          />
          {query && (
            <button
              onClick={handleClear}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#999',
                padding: 0, lineHeight: 1,
              }}
              type="button"
            >
              x
            </button>
          )}
          {searching && (
            <span style={{
              position: 'absolute', right: query ? 24 : 8, top: '50%', transform: 'translateY(-50%)',
              fontSize: 12, color: '#e94560',
            }}>...</span>
          )}
        </div>
        {showLocButton && (
          <button
            className="loc-btn"
            onClick={onLocClick}
            title="Use my location"
            type="button"
          >
            📍
          </button>
        )}
      </div>
      {value && (
        <span style={{ fontSize: 10, color: '#16a34a' }}>
          {value.lat.toFixed(5)}, {value.lon.toFixed(5)}
        </span>
      )}
      {showDropdown && results.length > 0 && (
        <ul className="geocode-dropdown">
          {results.map((r, i) => (
            <li
              key={i}
              onClick={() => handleSelect(r)}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f0f0'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {dropdownLabel(r.display_name)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
